/**
 * Expanded Care Info Types (Phase 2)
 *
 * Comprehensive care structure with detailed sections matching
 * the reference guide format in docs/Care Instructions Examples.md
 *
 * Display order:
 * 1. ☀️ Light
 * 2. 💧 Watering
 * 3. 🍼 Feeding
 * 4. 🪴 Potting & Soil
 * 5. 🥣 Soil Recipes
 * 6. ✂️ Pruning & Maintenance
 * 7. 🌱 Propagation
 * 8. 🚑 Troubleshooting & Signs
 * 9. 📅 Seasonal Care
 * 10. 📝 Notes
 */

import { z } from 'zod';

// ============================================================================
// Light Section
// ============================================================================

export interface LightInfo {
  /** Light requirement level (e.g., "High Light", "Bright Indirect") */
  requirement: string;
  /** Placement guidance (e.g., "South or West-facing window") */
  placement: string;
  /** Additional notes about light needs */
  notes?: string;
}

export const LightInfoSchema = z.object({
  requirement: z.string(),
  placement: z.string(),
  notes: z.string().optional(),
});

// ============================================================================
// Watering Section
// ============================================================================

export interface WateringSigns {
  /** Signs of underwatering (e.g., "Wrinkly leaves, dry soil") */
  underwatered: string;
  /** Signs of overwatering (e.g., "Yellow leaves, mushy stems") */
  overwatered: string;
}

export const WateringSignsSchema = z.object({
  underwatered: z.string(),
  overwatered: z.string(),
});

export interface WateringInfo {
  /** Watering method (e.g., "Soak and Dry", "Even Moisture") */
  method: string;
  /** Watering frequency or test (e.g., "Water when top 2-3 inches are dry") */
  frequency: string;
  /** Numeric watering interval in days (1-90), for smart scheduling */
  intervalDays?: number;
  /** Signs of under/overwatering */
  signs: WateringSigns;
  /** Dryness test description (e.g., "Check top 2 inches of soil") */
  drynessTest?: string;
  /** Water quality notes (e.g., "Let tap water sit 24hrs or use distilled") */
  qualityNotes?: string;
}

export const WateringInfoSchema = z.object({
  method: z.string(),
  frequency: z.string(),
  intervalDays: z.number().int().min(1).max(90).optional(),
  signs: WateringSignsSchema,
  drynessTest: z.string().optional(),
  qualityNotes: z.string().optional(),
});

// ============================================================================
// Feeding Section
// ============================================================================

export interface FeedingInfo {
  /** Fertilizer product type (e.g., "Standard Indoor Plant Food") */
  product: string;
  /** Feeding frequency (e.g., "Monthly during growing season") */
  frequency: string;
  /** Seasonal schedule or dilution notes */
  seasonalNotes?: string;
  /** Dilution ratio if applicable (e.g., "50% strength") */
  dilution?: string;
}

export const FeedingInfoSchema = z.object({
  product: z.string(),
  frequency: z.string(),
  seasonalNotes: z.string().optional(),
  dilution: z.string().optional(),
});

// ============================================================================
// Potting Section
// ============================================================================

export interface PottingInfo {
  /** Root preference (e.g., "Likes to be tight/root-bound") */
  rootPreference: string;
  /** When to repot (e.g., "Only when roots grow out of drainage holes") */
  repotCriteria: string;
  /** Pot sizing guidance (e.g., "Go 1-2 inches larger than root ball") */
  potSizing: string;
  /** Preferred pot shape (e.g., "Shallow, wide pots", "Standard") */
  potShape?: string;
  /** Drainage requirements */
  drainage: string;
  /** Additional potting notes */
  notes?: string;
}

export const PottingInfoSchema = z.object({
  rootPreference: z.string(),
  repotCriteria: z.string(),
  potSizing: z.string(),
  potShape: z.string().optional(),
  drainage: z.string(),
  notes: z.string().optional(),
});

// ============================================================================
// Soil Recipes Section
// ============================================================================

export interface SoilRecipe {
  /** Recipe name (e.g., "Standard Mix", "For Terra Cotta Pots") */
  name: string;
  /** Ingredients with ratios (e.g., ["50% Cactus Soil", "50% Perlite"]) */
  ingredients: string[];
  /** When to use this recipe */
  useCase?: string;
}

export const SoilRecipeSchema = z.object({
  name: z.string(),
  ingredients: z.array(z.string()),
  useCase: z.string().optional(),
});

// ============================================================================
// Pruning Section
// ============================================================================

export interface PruningInfo {
  /** Best timing for pruning (e.g., "Early Spring", "As needed") */
  timing: string;
  /** Pruning technique (e.g., "Cut back heavy branches", "Snip above leaf nodes") */
  technique: string;
  /** Additional pruning tips */
  tips?: string[];
  /** Extra notes */
  notes?: string;
}

export const PruningInfoSchema = z.object({
  timing: z.string(),
  technique: z.string(),
  tips: z.array(z.string()).optional(),
  notes: z.string().optional(),
});

// ============================================================================
// Propagation Section
// ============================================================================

export interface PropagationMethod {
  /** Method name (e.g., "Stem Cuttings (Water)", "Division", "Leaf Cuttings") */
  name: string;
  /** Step-by-step instructions */
  steps: string[];
  /** Success tips or warnings */
  successTips?: string;
}

export const PropagationMethodSchema = z.object({
  name: z.string(),
  steps: z.array(z.string()),
  successTips: z.string().optional(),
});

export interface PropagationInfo {
  /** Available propagation methods */
  methods: PropagationMethod[];
}

export const PropagationInfoSchema = z.object({
  methods: z.array(PropagationMethodSchema),
});

// ============================================================================
// Troubleshooting Section
// ============================================================================

export interface TroubleshootingItem {
  /** Symptom observed (e.g., "Leggy (Stretched)", "Yellow Leaves") */
  symptom: string;
  /** Likely cause (e.g., "Needs more light", "Overwatering") */
  cause: string;
  /** How to fix the issue */
  fix: string;
}

export const TroubleshootingItemSchema = z.object({
  symptom: z.string(),
  cause: z.string(),
  fix: z.string(),
});

// ============================================================================
// Seasonal Care Section
// ============================================================================

export interface SeasonalCare {
  /** Spring care notes (e.g., "Resume feeding, ideal repotting window") */
  spring?: string;
  /** Summer care notes (e.g., "Peak feeding, watch for pests") */
  summer?: string;
  /** Autumn care notes (e.g., "Taper off watering, stop feeding") */
  autumn?: string;
  /** Winter care notes (e.g., "Water sparingly, avoid cold drafts") */
  winter?: string;
}

export const SeasonalCareSchema = z.object({
  spring: z.string().optional(),
  summer: z.string().optional(),
  autumn: z.string().optional(),
  winter: z.string().optional(),
});

// ============================================================================
// Notes Section
// ============================================================================

export interface CareNotes {
  /** AI-generated additional observations */
  ai?: string;
  /** User's personal notes and observations */
  user?: string;
}

export const CareNotesSchema = z.object({
  ai: z.string().optional(),
  user: z.string().optional(),
});

// ============================================================================
// Expanded CareInfo (Complete Structure)
// ============================================================================

export interface ExpandedCareInfo {
  /** Schema version for backwards compatibility */
  version: 'v2';

  /** ☀️ Light requirements and placement */
  light: LightInfo;

  /** 💧 Watering method, frequency, and signs */
  watering: WateringInfo;

  /** 🍼 Feeding product and schedule */
  feeding: FeedingInfo;

  /** 🪴 Potting preferences and drainage */
  potting: PottingInfo;

  /** 🥣 Soil recipes with ingredients */
  soilRecipes: SoilRecipe[];

  /** ✂️ Pruning timing and technique */
  pruning: PruningInfo;

  /** 🌱 Propagation methods with steps */
  propagation: PropagationInfo;

  /** 🚑 Troubleshooting symptoms and fixes */
  troubleshooting: TroubleshootingItem[];

  /** 📅 Seasonal care rhythm */
  seasonal?: SeasonalCare;

  /** Simple text fields preserved from v1 */
  humidity: string;
  temperature: string;
  toxicity: string;

  /** 📝 Notes (AI and user) */
  notes?: CareNotes;
}

export const ExpandedCareInfoSchema = z.object({
  version: z.literal('v2'),
  light: LightInfoSchema,
  watering: WateringInfoSchema,
  feeding: FeedingInfoSchema,
  potting: PottingInfoSchema,
  soilRecipes: z.array(SoilRecipeSchema),
  pruning: PruningInfoSchema,
  propagation: PropagationInfoSchema,
  troubleshooting: z.array(TroubleshootingItemSchema),
  seasonal: SeasonalCareSchema.optional(),
  humidity: z.string(),
  temperature: z.string(),
  toxicity: z.string(),
  notes: CareNotesSchema.optional(),
});

// ============================================================================
// Type Guards and Utilities
// ============================================================================

/**
 * Check if care info is the expanded v2 format
 */
export function isExpandedCareInfo(careInfo: unknown): careInfo is ExpandedCareInfo {
  if (!careInfo || typeof careInfo !== 'object') return false;
  return (careInfo as { version?: string }).version === 'v2';
}

/**
 * Care version type for plant documents
 */
export type CareVersion = 'v1' | 'v2';

/**
 * Accordion section keys for expanded care (display order)
 */
export type ExpandedAccordionSection =
  | 'light'
  | 'watering'
  | 'feeding'
  | 'potting'
  | 'soilRecipes'
  | 'pruning'
  | 'propagation'
  | 'troubleshooting'
  | 'seasonal'
  | 'notes';

/**
 * Accordion state for expanded care sections
 */
export interface ExpandedAccordionState {
  light: boolean;
  watering: boolean;
  feeding: boolean;
  potting: boolean;
  soilRecipes: boolean;
  pruning: boolean;
  propagation: boolean;
  troubleshooting: boolean;
  seasonal: boolean;
  notes: boolean;
}

/**
 * Default accordion state - Light expanded by default
 */
export const DEFAULT_EXPANDED_ACCORDION_STATE: ExpandedAccordionState = {
  light: true,
  watering: false,
  feeding: false,
  potting: false,
  soilRecipes: false,
  pruning: false,
  propagation: false,
  troubleshooting: false,
  seasonal: false,
  notes: false,
};

/**
 * Section display configuration for UI
 */
export const CARE_SECTION_CONFIG: Record<
  ExpandedAccordionSection,
  { icon: string; label: string; order: number }
> = {
  light: { icon: 'wb_sunny', label: 'Light', order: 1 },
  watering: { icon: 'water_drop', label: 'Watering', order: 2 },
  feeding: { icon: 'nutrition', label: 'Feeding', order: 3 },
  potting: { icon: 'potted_plant', label: 'Potting & Soil', order: 4 },
  soilRecipes: { icon: 'compost', label: 'Soil Recipes', order: 5 },
  pruning: { icon: 'content_cut', label: 'Pruning & Maintenance', order: 6 },
  propagation: { icon: 'spa', label: 'Propagation', order: 7 },
  troubleshooting: { icon: 'healing', label: 'Troubleshooting', order: 8 },
  seasonal: { icon: 'calendar_month', label: 'Seasonal Care', order: 9 },
  notes: { icon: 'edit_note', label: 'Notes', order: 10 },
};
